<script setup>
import { computed } from 'vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import {
  CONDITION_MATCH_OPTIONS,
  CONDITION_OPERATORS,
  emptyRule,
  normalizeCondition,
  operatorNeedsValue,
} from '../conditionRules';

const props = defineProps({
  condition: { type: Object, default: null },
  fieldOptions: { type: Array, default: () => [] },
});

const emit = defineEmits(['update']);

// Interface texts kept in script, following the funnel page pattern.
const copy = {
  howItWorks: 'Como funciona',
  explanation:
    'SE as regras forem atendidas, o fluxo segue pelas ligações rotuladas “Sim”; SENÃO, pelas ligações “Não”. Textos são comparados sem diferenciar maiúsculas e acentos; “maior que” e “menor que” só comparam números. Sem regras, o nó continua apenas visual e segue por todas as ligações.',
  combineRules: 'Combinar regras',
  field: 'Campo',
  pickFromList: 'Escolher da lista',
  operator: 'Operador',
  value: 'Valor',
  noValueNeeded: 'Não precisa de valor.',
  noRules: 'Nenhuma regra ainda.',
};

const CUSTOM_FIELD = '__custom__';

const normalized = computed(() => normalizeCondition(props.condition));
const fieldChoices = computed(() => [
  ...props.fieldOptions,
  { value: CUSTOM_FIELD, label: 'Outro caminho (digitar)…' },
]);

const fieldLabel = value =>
  props.fieldOptions.find(option => option.value === value)?.label || value;

const isCustomField = rule =>
  rule.customField ||
  (rule.field &&
    !props.fieldOptions.some(option => option.value === rule.field));

const emitCondition = patch =>
  emit('update', { ...normalized.value, ...patch });

const updateRule = (index, patch) =>
  emitCondition({
    rules: normalized.value.rules.map((rule, position) =>
      position === index ? { ...rule, ...patch } : rule
    ),
  });

const selectField = (index, value) => {
  if (!value) return;
  if (value === CUSTOM_FIELD) {
    updateRule(index, { customField: true, field: '' });
    return;
  }
  updateRule(index, { field: value, customField: false });
};

const selectOperator = (index, value) => {
  if (!value) return;
  updateRule(
    index,
    operatorNeedsValue(value)
      ? { operator: value }
      : { operator: value, value: '' }
  );
};

const addRule = () =>
  emitCondition({ rules: [...normalized.value.rules, emptyRule()] });

const removeRule = index =>
  emitCondition({
    rules: normalized.value.rules.filter((_, position) => position !== index),
  });

const matchLabel = computed(
  () =>
    CONDITION_MATCH_OPTIONS.find(
      option => option.value === normalized.value.match
    )?.label || ''
);
</script>

<template>
  <section
    class="grid gap-3 rounded-lg border border-n-weak bg-n-solid-1 p-3"
    data-testid="funnel-condition-rules"
  >
    <div
      class="grid gap-1 rounded-lg bg-n-alpha-1 p-3 text-xs leading-5 text-n-slate-11"
    >
      <p class="mb-0 flex items-start gap-2 font-medium text-n-slate-12">
        <span class="i-hugeicons-git-branch size-4 shrink-0" />
        {{ copy.howItWorks }}
      </p>
      <p class="mb-0">
        {{ copy.explanation }}
      </p>
    </div>

    <div class="grid gap-1 sm:max-w-xs">
      <span class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ copy.combineRules }}
      </span>
      <ComboBox
        :model-value="normalized.match"
        :options="CONDITION_MATCH_OPTIONS"
        :display-label="matchLabel"
        placeholder="Selecione"
        search-placeholder="Buscar..."
        empty-state="Nenhum resultado encontrado."
        teleport-to-body
        @update:model-value="value => value && emitCondition({ match: value })"
      />
    </div>

    <ol class="mb-0 grid list-none gap-2 ps-0">
      <li
        v-for="(rule, index) in normalized.rules"
        :key="rule.id"
        class="grid gap-2 rounded-lg border border-n-weak p-3"
        data-testid="funnel-condition-rule"
      >
        <div class="flex items-center justify-between gap-2">
          <span class="text-xs font-semibold uppercase text-n-slate-10">
            {{
              index === 0 ? 'Se' : normalized.match === 'any' ? 'Ou se' : 'E se'
            }}
          </span>
          <NextButton
            type="button"
            size="xs"
            variant="ghost"
            color="ruby"
            icon="i-hugeicons-delete-02"
            :aria-label="`Remover regra ${index + 1}`"
            @click="removeRule(index)"
          />
        </div>
        <div class="grid gap-2 md:grid-cols-3">
          <div class="grid content-start gap-1">
            <span class="text-xs font-medium text-n-slate-11">{{
              copy.field
            }}</span>
            <Input
              v-if="isCustomField(rule)"
              :model-value="rule.field"
              placeholder="http.consulta.cliente.score"
              @update:model-value="value => updateRule(index, { field: value })"
            />
            <ComboBox
              v-else
              :model-value="rule.field"
              :options="fieldChoices"
              :display-label="rule.field ? fieldLabel(rule.field) : ''"
              placeholder="Selecione o campo"
              search-placeholder="Buscar campo..."
              empty-state="Nenhum campo encontrado."
              teleport-to-body
              @update:model-value="value => selectField(index, value)"
            />
            <button
              v-if="isCustomField(rule)"
              type="button"
              class="justify-self-start text-[11px] text-n-brand hover:underline"
              @click="updateRule(index, { customField: false, field: '' })"
            >
              {{ copy.pickFromList }}
            </button>
          </div>
          <div class="grid content-start gap-1">
            <span class="text-xs font-medium text-n-slate-11">{{
              copy.operator
            }}</span>
            <ComboBox
              :model-value="rule.operator"
              :options="CONDITION_OPERATORS"
              :display-label="
                CONDITION_OPERATORS.find(item => item.value === rule.operator)
                  ?.label || ''
              "
              placeholder="Operador"
              search-placeholder="Buscar..."
              empty-state="Nenhum resultado encontrado."
              teleport-to-body
              @update:model-value="value => selectOperator(index, value)"
            />
          </div>
          <div class="grid content-start gap-1">
            <span class="text-xs font-medium text-n-slate-11">{{
              copy.value
            }}</span>
            <Input
              v-if="operatorNeedsValue(rule.operator)"
              :model-value="rule.value"
              placeholder="ex.: 500, cliente_vip"
              @update:model-value="value => updateRule(index, { value })"
            />
            <p v-else class="mb-0 py-2 text-xs text-n-slate-10">
              {{ copy.noValueNeeded }}
            </p>
          </div>
        </div>
      </li>
    </ol>

    <p v-if="!normalized.rules.length" class="mb-0 text-xs text-n-slate-10">
      {{ copy.noRules }}
    </p>
    <div>
      <NextButton
        type="button"
        size="sm"
        variant="faded"
        color="slate"
        icon="i-hugeicons-add-01"
        label="Adicionar regra"
        @click="addRule"
      />
    </div>
  </section>
</template>
